import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { Monitor, Tablet, Smartphone, MessageSquare, Star, CheckCircle2, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/project")({
  component: ProjectPage,
});

const DEVICES = {
  desktop: { w: "100%", h: 720, icon: Monitor, label: "Desktop" },
  tablet: { w: 820, h: 700, icon: Tablet, label: "Tablet" },
  mobile: { w: 390, h: 720, icon: Smartphone, label: "Mobile" },
} as const;

function ProjectPage() {
  const qc = useQueryClient();
  const { data: project } = useProject();
  const [device, setDevice] = useState<keyof typeof DEVICES>("desktop");
  const [comment, setComment] = useState("");
  const [section, setSection] = useState("Hero");

  const { data: revisions } = useQuery({
    queryKey: ["portal", "revisions"],
    queryFn: async () => (await supabase.from("revisions").select("*").order("created_at", { ascending: false })).data ?? [],
    enabled: !!project,
  });
  const { data: events } = useQuery({
    queryKey: ["portal", "events"],
    queryFn: async () => (await supabase.from("calendar_events").select("*").order("event_date")).data ?? [],
    enabled: !!project,
  });
  const { data: feedback } = useQuery({
    queryKey: ["portal", "feedback"],
    queryFn: async () => (await supabase.from("project_feedback").select("*").limit(1).maybeSingle()).data,
    enabled: !!project,
  });

  const submitComment = async () => {
    if (!comment.trim() || !project) return;
    await supabase.from("revisions").insert({
      project_id: project.id, phase: section, title: `Commento su ${section}`,
      customer_notes: comment, status: "requested",
    });
    setComment("");
    toast.success("Commento inviato al team");
    qc.invalidateQueries({ queryKey: ["portal", "revisions"] });
  };

  const approve = async (id: string) => {
    await supabase.from("revisions").update({ status: "approved", approved_at: new Date().toISOString() }).eq("id", id);
    toast.success("Fase approvata");
    qc.invalidateQueries({ queryKey: ["portal", "revisions"] });
  };

  const submitFeedback = async (rating: number) => {
    if (!project) return;
    const { data: user } = await supabase.auth.getUser();
    await supabase.from("project_feedback").insert({
      project_id: project.id, user_id: user.user!.id, rating,
      google_review_prompted: rating >= 4,
    });
    toast.success(rating >= 4 ? "Grazie! Ti invitiamo a lasciarci una recensione Google" : "Grazie per il feedback");
    qc.invalidateQueries({ queryKey: ["portal", "feedback"] });
  };

  const online = project?.status === "online";

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Il mio sito</h1>
        <p className="text-white/60 mt-1">Preview live con commenti per sezione, revisioni e approvazioni.</p>
      </div>

      {online && (
        <div className={`${glass} p-6 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10`}>
          <div className="text-3xl">🎉 Il tuo sito è online!</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={project?.website_url ?? "#"} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-white text-black text-sm font-medium">Visita il sito</a>
            <a href={project?.cms_url ?? "#"} className="px-4 py-2 rounded-full bg-white/10 text-sm">Apri CMS</a>
            <a href="/portal/tickets" className="px-4 py-2 rounded-full bg-white/10 text-sm">Richiedi modifica</a>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className={`${glass} p-4 sm:p-6`}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex gap-1 p-1 rounded-full bg-white/5">
            {(Object.keys(DEVICES) as (keyof typeof DEVICES)[]).map((k) => {
              const D = DEVICES[k];
              return (
                <button key={k} onClick={() => setDevice(k)}
                  className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition ${
                    device === k ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                  <D.icon className="h-3.5 w-3.5" />{D.label}
                </button>
              );
            })}
          </div>
          <a href={project?.website_url ?? "#"} target="_blank" rel="noreferrer" className="text-xs text-white/60 hover:text-white">Apri in nuova tab ↗</a>
        </div>
        <div className="rounded-2xl bg-black/40 border border-white/10 p-3 overflow-auto grid place-items-center">
          <motion.div key={device} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            style={{ width: DEVICES[device].w, height: DEVICES[device].h }} className="max-w-full">
            <iframe title="preview" src={project?.website_url ?? "https://www.flash-web.ch"} className="w-full h-full rounded-xl border border-white/10 bg-white" />
          </motion.div>
        </div>
      </div>

      {/* Comment on section */}
      <div className={`${glass} p-6`}>
        <div className="flex items-center gap-2 mb-3"><MessageSquare className="h-4 w-4 text-fuchsia-300" /><h2 className="font-semibold">Commenta una sezione</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2">
          <select value={section} onChange={(e) => setSection(e.target.value)} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
            {["Hero", "Servizi", "Portfolio", "Testimonials", "Contatti", "Footer"].map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
          </select>
          <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Es. Cambia il colore del bottone in oro" className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          <button onClick={submitComment} className="px-4 py-2 rounded-xl bg-white text-black text-sm font-medium">Invia</button>
        </div>
        <div className="mt-4 space-y-2">
          {(revisions ?? []).map((r) => (
            <div key={r.id} className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-start gap-3">
              <span className={`mt-0.5 h-2 w-2 rounded-full ${r.status === "approved" ? "bg-emerald-400" : r.status === "in_progress" ? "bg-fuchsia-400" : "bg-white/30"}`} />
              <div className="flex-1">
                <div className="text-sm font-medium">{r.title} <span className="text-white/40 font-normal text-xs">· {r.phase}</span></div>
                {r.customer_notes && <div className="text-sm text-white/70 mt-0.5">{r.customer_notes}</div>}
                {r.developer_notes && <div className="text-xs text-cyan-300 mt-1">Nota developer: {r.developer_notes}</div>}
              </div>
              {r.status !== "approved" && (
                <button onClick={() => approve(r.id)} className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Approva</button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div className={`${glass} p-6`}>
        <div className="flex items-center gap-2 mb-4"><CalendarDays className="h-4 w-4 text-cyan-300" /><h2 className="font-semibold">Calendario progetto</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(events ?? []).map((e) => (
            <div key={e.id} className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="text-xs uppercase tracking-widest text-white/40">{e.event_type}</div>
              <div className="text-sm font-medium mt-1">{e.title}</div>
              <div className="text-xs text-white/60 mt-1">{format(new Date(e.event_date), "EEE d MMM", { locale: it })}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Feedback */}
      {!feedback && (
        <div className={`${glass} p-6 flex items-center justify-between gap-4 flex-wrap`}>
          <div>
            <div className="font-semibold">Come è andata l'esperienza?</div>
            <div className="text-sm text-white/60">Il tuo feedback aiuta il team a migliorare.</div>
          </div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => submitFeedback(n)} className="p-1 hover:scale-110 transition">
                <Star className="h-6 w-6 text-white/30 hover:text-amber-300" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
